/* eslint-disable max-lines */
import * as platform from 'platform'
import {Writable} from 'ts-essentials'
import {logger} from './logger'
import {Punycode} from './punycode'

// NOTE: \s is captured for urls to prevent them from capturing only parts of
// a text (like only domains in emails). Can't use lookbehind because it wasn't
// supported in Safari 13.

// todo: try rewriting to support all letter Unicode symbols in URLs using Unicode character classes,
//   see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Regular_expressions/Unicode_character_class_escape
export const urlRe =
  /(^|\s+)([a-z][a-z0-9]{1,15}:\/\/)?[a-z0-9а-я][a-z0-9а-я_:%+.~#=-]{0,255}\.[a-zа-я-]{2,16}(:\d{1,6})?(\/[a-z0-9а-я_@:%+.~#=?&/\-$!*'(),]*)?(?=(\s|$))/gi

export const punycodeUrlRe = /(^|\s+)xn--\S+(?=(\s|$))/gi

const uuidRegex = new RegExp(
  '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-' +
    '[89ab][0-9a-f]{3}-[0-9a-f]{12}',
  'i'
)
const shortedUuidRegex = new RegExp('[0-9a-f]{8}', 'i')

const alphabet = 'abcdefghijklmnopqrstuvwxyz'
const alphanum = alphabet + alphabet.toUpperCase() + '0123456789'
const aplhanumLower = alphabet + '0123456789'

/**
 *
 */
export type UUID = string & {__type: 'UUID'}

export type LongId = string & {__type: 'LongId'}

export class Util {
  static urlTemplate =
    "([\\s\\S]*?)(https?:\\/\\/[^' \\n]+[^\\(\\)\\[\\]>'\",. \\n\\t])([\\s\\S]*)"

  private static urlPattern = new RegExp(Util.urlTemplate)

  static urlRegex(): RegExp {
    Util.urlPattern.lastIndex = 0

    return Util.urlPattern
  }

  static iter<T>(obj: {length: number; [ix: number]: T}): Iterable<T> {
    function* gen(): IterableIterator<T> {
      if (!obj?.length) {
        return
      }
      // eslint-disable-next-line @typescript-eslint/prefer-for-of
      for (let i = 0; i < obj.length; i++) {
        yield obj[i]
      }
    }
    return gen()
  }

  static assert(condition: boolean, message: string): void {
    if (!condition) {
      throw new Error(message)
    }
  }

  // Эта функция специально сделана так, что
  // работает только для несериализуемых типов,
  // то есть для Set и Map она не должна работать
  static clone<T>(obj: T): Writable<T> {
    // if (isNotSerializable(obj)) {
    //   logger.error('Try to clone serializable object')
    // }
    if (typeof obj !== 'object' || !obj) {
      return obj
    }

    if (Array.isArray(obj)) {
      const newArr = []
      for (let i = 0; i < obj.length; i += 1) {
        newArr[i] = this.clone(obj[i])
      }
      return newArr as unknown as T
    }

    const newObj: any = {}
    for (const k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        newObj[k] = this.clone(obj[k])
      }
    }
    return newObj
  }

  static removeLeadingString(str: string, leading: string): string {
    if (!str) {
      return ''
    }
    return str.startsWith(leading) ? str.slice(leading.length) : str
  }

  static removeLeadingNewline(str: string): string {
    return this.removeLeadingString(str, '\n')
  }

  static serializeStringMap<T>(map: Map<string, T>): {[key: string]: T} {
    const res: {[key: string]: T} = {}
    map.forEach((val: T, key: string) => {
      res[key] = typeof val === 'object' ? (Util.clone(val) as T) : val
    })
    return res
  }

  static deserializeStringMap<T>(json: {[key: string]: T}): Map<string, T> {
    if (json && typeof json === 'object') {
      const res: Map<string, T> = new Map()
      for (const key in json) {
        if (!Object.prototype.hasOwnProperty.call(json, key)) {
          continue
        }
        if (typeof json[key] === 'object') {
          res.set(key, Util.clone(json[key]) as T)
        } else {
          res.set(key, json[key])
        }
      }
      return res
    } else {
      return new Map()
    }
  }

  static serializeStringSet(set: Set<string>): {[key: string]: number} {
    const res: {[key: string]: number} = {}
    set.forEach((key: string) => (res[key] = 1))
    return res
  }

  static deserializeStringSet(
    json: {[key: string]: number} | string[]
  ): Set<string> {
    if (json instanceof Array) {
      // deprecated
      return new Set(json)
    } else if (json && typeof json === 'object') {
      return new Set(Object.keys(json))
    } else {
      logger.warn('empty variable as Set')
      return new Set()
    }
  }

  /**
   * Useful with React
   */
  static className(classes: {[key: string]: boolean}): string {
    return Object.keys(classes)
      .filter((cl: string) => classes[cl])
      .join(' ')
  }

  /**
   * Example:
   * Util.bem(
   *   'panel', 'button', 'icon',
   *   {active: true, hover: false},
   *   ['some']
   * );
   * returns string:
   *   'panel__button__icon panel__button__icon--active some__button__icon some__button__icon--active'
   */
  static bem(
    block: string,
    ...elementsOrModifiers: Array<string | {[mod: string]: boolean} | string[]>
  ): string {
    const baseClassNames: string[] = [block]
    const elements: string[] = []
    const modList: string[] = []

    for (const em of elementsOrModifiers) {
      if (!em) {
        continue
      }
      if (typeof em === 'string') {
        elements.push(em)
      } else if (em instanceof Array) {
        em.filter(c => !!c).forEach(c => baseClassNames.push(c))
      } else if (typeof em === 'object') {
        Object.keys(em).forEach(m => {
          if (em[m]) {
            modList.push(Util.camelToKebab(m))
          }
        })
      } else {
        logger.error('Unsupported argument type')
      }
    }

    const classNames = baseClassNames.map(c => [c, ...elements].join('__'))
    const classList = [...classNames]
    for (const mod of modList) {
      classNames.forEach(c => classList.push(c + '--' + mod))
    }
    return classList.join(' ')
  }

  static bemFrom =
    (
      block: string,
      ...args: Array<string | {[mod: string]: boolean} | string[]>
    ) =>
    (...args2: Array<string | {[mod: string]: boolean} | string[]>) => {
      return Util.bem(block, ...args, ...args2)
    }

  static range(n1: number, n2?: number, n3?: number): number[] {
    if (typeof n1 === 'undefined') {
      logger.error('Util.range: incorrect arguments')
      return []
    }
    if (typeof n2 === 'undefined') {
      n2 = n1
      n1 = 0
    }
    if (n1 === n2) {
      return []
    }
    if (typeof n3 === 'undefined') {
      n3 = Math.sign(n2 - n1)
    }
    if (n3 === 0 || Math.sign(n3) !== Math.sign(n2 - n1)) {
      logger.error('Util.range: incorrect increment')
      return []
    }
    const res: number[] = []
    const direction = Math.sign(n3)
    for (let i = n1; direction > 0 ? i < n2 : i > n2; i += n3) {
      res.push(i)
    }
    return res
  }

  static colorToRGB(hex: string): [number, number, number] {
    if (!hex) {
      return [0, 0, 0]
    }
    hex = hex.replace(/^#([a-f\d])([a-f\d])([a-f\d])$/i, '#$1$1$2$2$3$3')
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? [
          parseInt(result[1], 16),
          parseInt(result[2], 16),
          parseInt(result[3], 16)
        ]
      : [0, 0, 0]
  }

  static rgbToHex(rgb: [number, number, number]): string {
    const toHex = (c: number): string => ('0' + c.toString(16)).slice(-2)
    return '#' + toHex(rgb[0]) + toHex(rgb[1]) + toHex(rgb[2])
  }

  static mixColors(hex1: string, hex2: string, balance: number): string {
    const rgb1 = Util.colorToRGB(hex1)
    const rgb2 = Util.colorToRGB(hex2)
    return Util.rgbToHex([
      Math.min(Math.round(rgb1[0] * balance + rgb2[0] * (1 - balance)), 255),
      Math.min(Math.round(rgb1[1] * balance + rgb2[1] * (1 - balance)), 255),
      Math.min(Math.round(rgb1[2] * balance + rgb2[2] * (1 - balance)), 255)
    ])
  }

  static amountToStr(amount: number): string {
    const str = amount + ''
    const p1 = str.split('').slice(-3).join('')
    const p2 = str.split('').slice(-6, -3).join('')
    const p3 = str.split('').slice(0, -6).join('')
    if (p3) {
      return [p3, p2, p1].join(' ') // nbsp
    } else if (p2) {
      return [p2, p1].join(' ') // nbsp
    } else {
      return p1
    }
  }

  /**
   * Deprecated. UUIDs will be replaced with longIds.
   */
  static UUID(): UUID {
    const hex = '01234567890abcdef'
    const y = '89ab'
    const pick = (str: string): string => {
      return str.charAt(Math.floor(Math.random() * str.length))
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      (ch: string) => (ch === 'y' ? pick(y) : pick(hex))
    ) as UUID
  }

  static shortId(uuid: UUID): string {
    return uuid?.split('-')?.pop() ?? ''
  }

  static randomAlphanum(len: number, onlyLowercase = false): string {
    const letters = onlyLowercase ? aplhanumLower : alphanum
    const l = letters.length
    return Util.range(len)
      .map(() => letters[Math.floor(Math.random() * l)])
      .join('')
  }

  static randomAlpha(len: number): string {
    const l = alphabet.length
    return Util.range(len)
      .map(() => alphabet[Math.floor(Math.random() * l)])
      .join('')
  }

  static makeShortId(): string {
    const hex = '01234567890abcdef'
    const len = hex.length
    const res = []
    for (let i = 0; i < 12; i++) {
      res.push(hex[Math.floor(Math.random() * len)])
    }
    return res.join('')
  }

  static isShortId(str: string): boolean {
    return typeof str === 'string' && str.length === 12
  }

  /**
   * Shorter (2 times more memory and disk efficient),
   * more readable than UUID and faster to generate and check.
   * Saves about 30%-50% of database space after switching to it.
   * Old clients are still checking if variable is UUID, so can fully switch
   * to longIds only after some time.
   */
  static longId(): LongId {
    const len = alphanum.length
    const res = []
    for (let i = 0; i < 16; i++) {
      res.push(alphanum[Math.floor(Math.random() * len)])
    }
    return res.join('') as LongId
  }

  static isLongId(id: string | undefined): boolean {
    return typeof id === 'string' && id.length === 16
  }

  static isUUID(uuid: string | undefined): boolean {
    if (typeof uuid !== 'string' || !uuid) {
      return false
    }
    return !!uuid.match(uuidRegex)
  }
  static isShortedUUID(uuid: string): boolean {
    if (typeof uuid !== 'string' || !uuid) {
      return false
    }
    return !!uuid.match(shortedUuidRegex)
  }

  /**
   * Deprecated. UUIDs will be replaced with longIds.
   * This is a temporary replacement of isUUID.
   */
  static isIdOrUUID(str: string | undefined): boolean {
    return Util.isLongId(str) || Util.isUUID(str)
  }

  private static emailRegEx =
    /^[-a-zа-я0-9~!$%^&*_=+}{'?]+(\.[-a-zа-я0-9~!$%^&*_=+}{'?]+)*@([a-zа-я0-9_][-a-zа-я0-9_]*(\.[-a-zа-я0-9_]+)*\.([a-zа-я]{2,15}))$/i
  static isEmail(email: unknown): email is string {
    return !!email && typeof email === 'string' && Util.emailRegEx.test(email)
  }

  static isString(s: unknown): s is string {
    return typeof s === 'string' || s instanceof String
  }

  static isPhone(phone: unknown): phone is string {
    return (
      !!phone &&
      typeof phone === 'string' &&
      phone.replace(/[^0-9]/g, '').length > 7
    )
  }

  static removeDuplicateIds<T extends {id: UUID}>(arr: T[]): T[] {
    const map: Map<UUID, T> = new Map()
    arr.forEach((el: T) => {
      if (el) {
        map.set(el.id, el)
      }
    })
    return [...map.values()]
  }

  static removeDuplicates(arr: string[]): string[] {
    const removeDuplicates = arr.reduce(
      (acc, item) => {
        acc[item] = true
        return acc
      },
      {} as {[key: string]: boolean}
    )

    return Object.keys(removeDuplicates).filter(val => !!val)
  }

  static limitStr(str: string, limit: number): string {
    if (!str || !limit) {
      return str
    }
    return str.length <= limit ? str : str.slice(0, limit - 3) + '…'
  }

  static sameEmail(email1: string, email2: string): boolean {
    if (!email1 || !email2) {
      return false
    }
    return Util.cleanEmail(email1) === Util.cleanEmail(email2)
  }

  static cleanEmail(email: string | undefined): string {
    if (!email) {
      return ''
    }
    try {
      if (email.includes('@')) {
        const [user, domain, ...rest] = email.split('@')
        return [user, Punycode.safeDecode(domain.trim()), ...rest]
          .join('@')
          .trim()
          .toLowerCase()
      }
    } catch (e) {
      //
    }
    return email.trim().toLowerCase()
  }

  static humanReadableSize(bytes: number): string {
    if (!bytes) {
      return '0B'
    }
    let i = 0
    const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    while (bytes > 1000) {
      bytes = bytes / 1000
      i++
    }
    let byteStr = bytes.toFixed(1)
    if (byteStr.length > 3) {
      byteStr = bytes.toFixed(0)
    }
    return (byteStr + units[i]).replace('.0B', 'B')
  }

  static camelToSnake(str: string): string {
    return str
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '')
  }

  static camelToKebab(str: string): string {
    return str
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()
      .replace(/^-/, '')
  }

  static snakeToCamel(str: string): string {
    return str.replace(/_(.)/g, (_, g) => g.toUpperCase())
  }

  static kebabToCamel(str: string): string {
    return str.replace(/-(.)/g, (_, g) => g.toUpperCase())
  }

  static kebabToPascal(str: string): string {
    return Util.kebabToCamel(str).replace(/^(.)/g, (_, g) => g.toUpperCase())
  }

  static negate<T extends (...args: unknown[]) => boolean>(
    fn: (...args: Parameters<T>) => boolean
  ): (...args: Parameters<T>) => boolean {
    return (...args: Parameters<T>) => !fn(...args)
  }

  static isDesktop(): boolean {
    return location.protocol === 'file:'
  }

  static getClientType(): 'desktop' | 'web' {
    return Util.isDesktop() ? 'desktop' : 'web'
  }
  static isOSX(): boolean {
    return navigator && navigator.platform === 'MacIntel'
  }

  static isServer(): boolean {
    return !(typeof window === 'object' && !!window.document)
  }

  static isInside(
    rect: {x: number; y: number; width: number; height: number},
    point: {x: number; y: number}
  ): boolean {
    const {x, y} = point

    return (
      x > rect.x &&
      y > rect.y &&
      x <= rect.x + rect.width &&
      y <= rect.y + rect.height
    )
  }

  static uniq<T>(list: T[]): T[] {
    return [...new Set(list)]
  }

  static noop(..._: unknown[]): void {
    // do nothing
  }

  static notInitialized(): never {
    throw new Error('Not initialized')
  }

  static async asyncReportLong<T>(
    name: string,
    maxTime: number,
    code: () => Promise<T>
  ): Promise<T> {
    const t1 = Date.now()
    const res = await code()
    const t = Date.now() - t1
    if (t > maxTime) {
      logger.warn(`Long operation “${name}” ${t} ms`)
    }
    return res
  }

  static reportLong<T>(name: string, maxTime: number, code: () => T): T {
    const t1 = Date.now()
    const res = code()
    const t = Date.now() - t1
    if (t > maxTime) {
      logger.warn(`Long operation “${name}” ${t} ms`)
    }
    return res
  }

  static itemsWithUniqueSuffixes(
    items: string[],
    generateSuffix: (id: number) => string = (id: number) => ` (${id})`
  ): string[] {
    const suffixes = new Map()

    return items.map((item: string) => {
      const suffix = suffixes.get(item)
      suffixes.set(item, (suffix || 0) + 1)

      return item + (suffix ? generateSuffix(suffix) : '')
    })
  }

  static reverse(text: string): string {
    return text.split('').reverse().join('')
  }

  static identity<T>(x: T): T {
    return x
  }

  static distance(dx: number, dy: number): number {
    return Math.sqrt(dx * dx + dy * dy)
  }

  static millisecondsToHours(milliseconds: number): number {
    return milliseconds / 3600000
  }

  static withoutTime(date: Date): Date {
    const d = new Date(date.getTime())
    d.setHours(0)
    d.setMinutes(0)
    d.setSeconds(0)
    d.setMilliseconds(0)
    return d
  }

  static pointToArray({x, y}: {x: number; y: number}): [number, number] {
    return [x, y]
  }

  private static timeout64Max = 9223372036854775000
  private static timeout64Step = 2147483640
  private static timeout64StepCurrentId = 0
  private static timeout64Registered: Map<Timer64Id, any> = new Map()

  /**
   * setTimeout takes only int32 delays (which is only about 25 days).
   * setTimeout64 takes delays up to maxSetTimeout64 which
   * is more than 292M years.
   */
  static setTimeout64(
    cb: () => void,
    delay: number,
    replaceId?: Timer64Id
  ): Timer64Id {
    if (delay > Util.timeout64Max) {
      delay = Util.timeout64Max
    }
    if (!replaceId) {
      Util.timeout64StepCurrentId++
    }
    const id = replaceId || (Util.timeout64StepCurrentId as Timer64Id)

    if (delay < Util.timeout64Step) {
      const timer = setTimeout(() => {
        Util.timeout64Registered.delete(id)
        cb()
      }, delay)
      Util.timeout64Registered.set(id, timer)
    } else {
      const remaining = delay - Util.timeout64Step
      const timer = setTimeout(() => {
        Util.setTimeout64(cb, remaining, id)
      }, Util.timeout64Step)
      Util.timeout64Registered.set(id, timer)
    }
    return id
  }

  static clearTimeout64(id: Timer64Id): void {
    if (!id) {
      return
    }
    const timer = Util.timeout64Registered.get(id)
    if (timer) {
      clearTimeout(timer)
    }
    Util.timeout64Registered.delete(id)
  }

  static capitalize(str: string): string {
    return str.length !== 0 ? str[0].toLocaleUpperCase() + str.slice(1) : str
  }

  /**
   * Given an array of N sets of some elements returns an array of numbers
   * describing intersections of these sets:
   * result[0] = total number of unique elements in union of all sets
   * result[n where n>0] = total number of elements in intersection of
   * sets that are marked by the bits of the number n.
   *
   * -- this provides a complete numerical picture of the specified sets.
   */
  static describeSets<T>(sets: Array<Set<T>>): number[] {
    const result: number[] = []
    const len = 1 << sets.length
    const all = Util.union(...sets)

    for (let i = 0; i < len; i++) {
      const chosen = sets.filter((_: Set<T>, ix: number) => {
        return !!(i & (1 << ix))
      })
      const intersection = Util.intersect(all, ...chosen)
      result[i] = intersection.size
    }
    return result
  }

  static union<T>(...sets: Array<Set<T>>): Set<T> {
    let res: Set<T> = new Set()
    sets.forEach(s => (res = new Set([...res, ...s])))
    return res
  }

  static intersect<T>(...sets: Array<Set<T>>): Set<T> {
    let res = [...sets[0]]
    for (let i = 1; i < sets.length; i++) {
      const s = sets[i]
      res = res.filter(x => s.has(x))
    }
    return new Set(res)
  }

  static checksum(str: string): number {
    if (!str) {
      return 0
    }
    return str
      .split('')
      .map(s => s.charCodeAt(0))
      .reduce((a, b) => a + b)
  }

  static escapeRegexp(s: string): string {
    return s
      .replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')
      .replace(/&nbsp;/g, '(\\s|&nbsp;)')
  }

  static wait(timeMs: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, timeMs))
  }

  static afterTimeout<T>(cb: () => T, timeMs: number): Promise<T> {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          resolve(cb())
        } catch (e) {
          logger.log('Error in afterTimeout', e)
          reject()
        }
      }, timeMs)
    })
  }

  static catch<T>(cb: () => T): T | undefined {
    try {
      return cb()
    } catch (e) {
      logger.error(e)
      return undefined
    }
  }

  static checkWildcard(wildcard: string, value: string): boolean {
    const starIx = wildcard.indexOf('*')
    if (starIx === -1) {
      return wildcard === value
    } else {
      const before = wildcard.slice(0, starIx)
      const after = wildcard.slice(starIx + 1)
      if (!!before && !value.startsWith(before)) {
        return false
      }
      const valueAfter = value.slice(before.length)
      if (!after.includes('*')) {
        return (
          !after ||
          (valueAfter.includes(after) &&
            after.length + valueAfter.lastIndexOf(after) === valueAfter.length)
        )
      } else {
        const indexes = Util.getAllIndexes(valueAfter, after.split('*')[0])
        return (
          indexes.findIndex(ix =>
            Util.checkWildcard(after, valueAfter.slice(ix))
          ) > -1
        )
      }
    }
  }

  static getAllIndexes(source: string, target: string, fromIx = 0): number[] {
    const first = source.indexOf(target)
    if (first === -1) {
      return []
    } else {
      for (let ix = 1; ix < target.length; ix++) {
        if (target.startsWith(target.slice(ix))) {
          return [
            first + fromIx,
            ...Util.getAllIndexes(
              source.slice(first + ix),
              target,
              first + ix + fromIx
            )
          ]
        }
      }
      return [
        first + fromIx,
        ...Util.getAllIndexes(
          source.slice(first + target.length),
          target,
          first + target.length + fromIx
        )
      ]
    }
  }

  static safeDecodeUriComponent(uri: string, fallback: string = uri): string {
    try {
      return decodeURIComponent(uri)
    } catch (e) {
      return fallback
    }
  }

  static simpleHashCode(s: string): number {
    let hash = 0
    const len = s.length
    for (let i = 0; i < len; i++) {
      const char = s.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32bit integer
    }
    return hash
  }

  // Simple implementation of lodash.get
  // Handles arrays, objects, and any nested combination of the two.
  // Get from: https://gist.github.com/andrewchilds/30a7fb18981d413260c7a36428ed13da
  static deepGet<T = any>(obj: unknown, query: string | string[]): T | undefined
  static deepGet<T = any>(
    obj: unknown,
    query: string | string[],
    defaultValue: T
  ): T
  static deepGet<T = any>(
    obj: unknown,
    query: string | string[],
    defaultValue?: T
  ): T | undefined {
    query = Array.isArray(query)
      ? query
      : query
          .replace(/(\[(\d)\])/g, '.$2')
          .replace(/^\./, '')
          .split('.')
    const o = obj as Record<string, unknown>
    if (!(query[0] in o)) {
      return defaultValue
    }
    obj = o[query[0]]
    if (obj && query.length > 1) {
      return this.deepGet(obj, query.slice(1), defaultValue)
    }
    return obj as T
  }

  static isDefined<T>(obj: T | undefined | null | false): obj is T {
    return !!obj
  }

  static extractErrorMessage(e: unknown): string {
    if (typeof e === 'string') {
      return e
    } else if (e instanceof Error) {
      return e.message
    } else {
      return ''
    }
  }

  static isMac(): boolean {
    const family = platform?.os?.family?.toLowerCase()
    if (!family) {
      logger.error(
        `Can't detect platform type because of platform object is undefined`
      )
      return false
    }
    return family.includes('os x') || family.includes('mac')
  }

  static lerp(left: number, right: number, p: number) {
    p = Math.max(0, Math.min(1, p))
    return (1 - p) * left + p * right
  }

  static ipRegex = new RegExp(
    '^(http(s)?\\:\\/\\/)?((25[0-5]|(2[0-4]|1\\d|[1-9]|)\\d)\\.?\\b){4}(\\:\\d+)?$',
    'gi'
  )

  static isIP(ip: string): boolean {
    return !!ip.match(Util.ipRegex)
  }

  static truncateString(value: unknown, maxLength = 30000): unknown {
    if (typeof value === 'string' && value.length > maxLength) {
      return value.substring(0, maxLength) + '…'
    }
    return value
  }
}

export type Timer64Id = number & {__type: 'Timer64Id'}

export const isNotSerializable = (obj: unknown): boolean => {
  if (obj instanceof Map || obj instanceof Set) {
    return true
  }

  if (typeof obj !== 'object') {
    return false
  }

  const obj2 = obj as {[key: string]: unknown}
  for (const property in obj2) {
    // eslint-disable-next-line no-prototype-builtins
    if (obj2.hasOwnProperty(property)) {
      if (typeof obj2[property] !== 'object') {
        continue
      }
      if (isNotSerializable(obj2[property])) {
        return true
      }
    }
  }

  return false
}
